import { Box, InputAdornment, TextField, Typography } from '@mui/material'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import { format } from 'date-fns'
import { Position, Template } from 'interface'
import { useEffect, useState } from 'react'
import CanvasImg from './CanvasImg'

import { yupResolver } from '@hookform/resolvers/yup'
import CategoryIcon from '@mui/icons-material/Category'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import QuizIcon from '@mui/icons-material/Quiz'
import Switch from '@mui/material/Switch'
import { useSnackbar } from 'notistack'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: 8,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 8,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '#ff03',
  },
}))

const MAX_ITEM_IN_PAGE = 10

interface Props {
  data: Template[]
}

interface FormInput {
  name: string
  qText: string
  hint: string
  usable: boolean
}
const schema = yup.object({
  name: yup.string().required(),
  qText: yup.string(),
  hint: yup.string(),
  usable: yup.boolean(),
})

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#1890ff',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,.35)'
        : 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}))

export default function QuizTable({ data }: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const [selected, setSelected] = useState<Template>()
  const [loading, setLoading] = useState(false)
  const [showImage, setShowImage] = useState<{
    imgPath: string
    positions: Position[]
  }>()
  const [pagination, setPagination] = useState({
    curPage: 1,
    totalPage: 0,
  })
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      usable: true,
    },
  })
  useEffect(() => {
    setPagination({
      ...pagination,
      totalPage: Math.ceil(data.length / MAX_ITEM_IN_PAGE),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination({
      ...pagination,
      curPage: value,
    })
  }
  const onSubmitUpdate: SubmitHandler<FormInput> = async (data) => {
    try {
      setLoading(true)
      const { name, hint, qText, usable } = data
      await axiosClient.post('/api/templates/update/' + selected?.id, {
        name,
        hint,
        qText,
        usable,
      })
      enqueueSnackbar('変更しました。', {
        variant: 'success',
      })
      reset()
      setSelected(undefined)
    } catch (error) {
      enqueueSnackbar('エラーが発生しました。', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }
  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 768 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>No</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 80 }}>タイトル</StyledTableCell>
              <StyledTableCell>問題文</StyledTableCell>
              <StyledTableCell>画像</StyledTableCell>
              <StyledTableCell>ヒント</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 80 }}>状態</StyledTableCell>
              <StyledTableCell>登録日</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(
                (pagination.curPage - 1) * MAX_ITEM_IN_PAGE,
                pagination.curPage * MAX_ITEM_IN_PAGE
              )
              .map((temp, i) => {
                const time = new Date(temp.createdAt).getTime()
                return (
                  <StyledTableRow
                    key={i}
                    onClick={() => {
                      setValue('name', temp.name)
                      setValue('qText', temp.qText)
                      setValue('hint', temp.hint)
                      setValue('usable', !!temp.usable)
                      setSelected(temp)
                    }}
                  >
                    <StyledTableCell>{i+1}</StyledTableCell>
                    <StyledTableCell>{temp.name}</StyledTableCell>
                    <StyledTableCell>{temp.qText}</StyledTableCell>
                    <StyledTableCell
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowImage({
                          imgPath: temp.qImage,
                          positions: temp.positions,
                        })
                      }}
                    >
                      <CanvasImg
                        imgPath={temp.qImage}
                        positions={temp.positions}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{temp.hint}</StyledTableCell>
                    <StyledTableCell>
                      {temp.usable ? '使用可' : '使用不可'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {format(time, 'yyyy/MM/dd')}
                    </StyledTableCell>
                  </StyledTableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack my={1} alignItems="center">
        <Pagination
          count={pagination.totalPage}
          page={pagination.curPage}
          shape="rounded"
          onChange={handleChange}
        />
      </Stack>
      <DialogConfirm
        open={!!showImage}
        handleClose={() => setShowImage(undefined)}
        primaryButton={false}
        title=""
      >
        <Stack alignItems="center">
          <CanvasImg
            imgPath={showImage?.imgPath}
            positions={showImage?.positions}
          />
        </Stack>
      </DialogConfirm>
      <DialogConfirm
        open={!!selected}
        handleClose={() => setSelected(undefined)}
        handleSubmit={handleSubmit(onSubmitUpdate)}
        primaryButtonText="変更する"
        loading={loading}
        title="問題テンプレート修正"
        maxWidth="md"
      >
        <Box component="form" noValidate autoComplete="off">
          <Stack direction="row" spacing={1} my={1} alignItems="center">
            <Typography fontWeight="bold" mr={2}>
              状態
            </Typography>
            <Typography>使用不可</Typography>
            <AntSwitch
              checked={getValues('usable')}
              inputProps={{ 'aria-label': 'ant design' }}
              onChange={(e) => {
                setValue('usable', e.target.checked, {
                  shouldValidate: true,
                })
              }}
            />
            <Typography>使用可</Typography>
          </Stack>
          <TextField
            required
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon color="primary" />
                </InputAdornment>
              ),
            }}
            label="テンプレートタイトル"
            fullWidth
            variant="standard"
            {...register('name')}
            error={'name' in errors}
          />
          <TextField
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QuizIcon color="primary" />
                </InputAdornment>
              ),
            }}
            label="問題文（任意）"
            fullWidth
            variant="standard"
            {...register('qText')}
            error={'qText' in errors}
          />
          <TextField
            margin="dense"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LightbulbOutlinedIcon color="primary" />
                </InputAdornment>
              ),
            }}
            label="ヒント（任意）"
            fullWidth
            variant="standard"
            {...register('hint')}
            error={'hint' in errors}
          />
        </Box>
      </DialogConfirm>
    </>
  )
}
